import type { FunctionComponent } from 'react'
import React from 'react'

import { TypographyV2 } from '../../../Foundations/Typography'
import styles from './Heading.module.scss'

/**
 * Heading Component
 *
 * @note: `overline` and `subheading` props are not available when `headingType` is `'small'`
 */
export const Heading: FunctionComponent<HeadingBlockProps> = (props) => {
  const { headingTag, headingType, heading } = props

  return (
    <div className={styles.headingBlock}>
      <TypographyV2
        className={styles.headingBlockHeading}
        tag={headingTag}
        textStyle={`sb-text-heading-${headingType}`}
      >
        {heading}
      </TypographyV2>

      {headingType !== 'small' && props.overline ? (
        <TypographyV2
          className={styles.headingBlockOverline}
          tag={'p'}
          textStyle="sb-text-heading-overline"
        >
          {props.overline}
        </TypographyV2>
      ) : null}

      {headingType !== 'small' && props.subHeading ? (
        <TypographyV2
          className={styles.headingBlockSubHeading}
          textStyle="sb-text-heading-subheading"
          tag={'p'}
        >
          {props.subHeading}
        </TypographyV2>
      ) : null}
    </div>
  )
}

type HeadingBlockBaseProps = {
  /**
   * Heading Text
   */
  heading: string
  /**
   * Heading Tag
   */
  headingTag: ValidTitleTags
}

type SmallHeadingBlockProps = HeadingBlockBaseProps & {
  /**
   * Heading Type
   * @type `small` | `medium` | `large`
   * @note `overline` and `subheading` props are not available when `headingType` is `'small'`
   */
  headingType: 'small'
}

type LargeHeadingBlockProps = HeadingBlockBaseProps & {
  /**
   * Heading Type
   * @type `small` | `medium` | `large`
   * @note `overline` and `subheading` props are not available when `headingType` is `'small'`
   */
  headingType: 'medium' | 'large'
  /**
   * Overline Text
   * @note: `overline` is not available when `headingType` is `'small'`
   */
  overline?: string
  /**
   * Subheading Text
   * @note: `subheading` is not available when `headingType` is `'small'`
   */
  subHeading?: string
}

export type HeadingBlockProps = SmallHeadingBlockProps | LargeHeadingBlockProps

type ValidTitleTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
